import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Buttons, CancelButton, Icon, SingleSelect, TextInput } from '~/components';
import { useApi, useSession } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import { colors, weights } from '~/styles';
import { PageLoader } from '../pages';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 1.5rem;
`;

const Card = styled.div`
  width: 100%;
  max-width: 30rem;
  padding: 2rem;
  background-color: ${colors.white};
  border: 1px solid ${colors.grey10};
  border-radius: 0.625rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: ${weights.light};
  line-height: 1.3;
  text-align: center;
`;

const ClientName = styled.span`
  font-weight: ${weights.bold};
`;

const Unverified = styled.div`
  margin-top: 0.5rem;
  color: ${colors.grey55};
  font-size: 0.75rem;
  text-align: center;
`;

const Section = styled.div`
  margin-top: 2rem;
`;

const SectionLabel = styled.div`
  margin-bottom: 0.5rem;
  color: ${colors.grey55};
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  letter-spacing: 0.05rem;
  text-transform: uppercase;
`;

const Scope = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 0.75rem;
  font-size: 0.875rem;
`;

const ScopeIcon = styled(Icon)`
  margin-top: 0.15rem;
  margin-right: 0.625rem;
  color: ${colors.primary};
`;

const CodeInput = styled(TextInput)`
  font-family: monospace;
  font-size: 1.5rem;
  letter-spacing: 0.25rem;
  text-align: center;
  text-transform: uppercase;
`;

const Message = styled.p`
  margin-top: 1rem;
  color: ${colors.danger};
  font-size: 0.875rem;
`;

const Note = styled.p`
  margin-top: 2rem;
  color: ${colors.grey55};
  font-size: 0.8125rem;
  line-height: 1.5;
`;

const Done = styled.div`
  margin-top: 2rem;
  font-size: 0.9375rem;
  line-height: 1.6;
  text-align: center;
`;

const DoneIcon = styled(Icon)`
  display: block;
  margin: 0 auto 1rem;
  color: ${colors.primary};
  font-size: 2.5rem;
`;

const Actions = styled.div`
  margin-top: 2rem;
`;

export default function AuthorizeDevice() {
  useDocumentTitle('Connect a Device');

  const api = useApi();
  const history = useHistory();
  const location = useLocation();
  const { isReady: isSessionReady, session } = useSession();

  // A client that can render a link sends the member here with the code already filled in.
  const initialCode = new URLSearchParams(location.search).get('user_code') ?? '';

  const [userCode, setUserCode] = useState(initialCode);
  const [request, setRequest] = useState();
  const [workspaceId, setWorkspaceId] = useState('');
  const [outcome, setOutcome] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isSessionReady) return;

    // Approving is an authenticated act, so sign in first and come back to the same code.
    if (!session) {
      const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
      history.replace(`/login?redirect=${redirect}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSessionReady, session]);

  const lookup = useCallback(
    async (code) => {
      setIsSubmitting(true);
      setErrorMessage();

      try {
        const { data } = await api.www.oauth().deviceRequest({ userCode: code });
        setRequest(data);
        if (data.workspaces.length === 1) setWorkspaceId(data.workspaces[0].id);
      } catch ({ message }) {
        setErrorMessage(message || 'That code is not valid. It may have expired.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [api],
  );

  useEffect(() => {
    if (!session || !initialCode) return;
    lookup(initialCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, initialCode]);

  async function decide(approved) {
    setIsSubmitting(true);
    setErrorMessage();

    try {
      const { data } = await api.www.oauth().deviceDecision({ userCode, approved, workspaceId });
      setOutcome(data);
    } catch ({ message }) {
      setErrorMessage(message || 'There was a problem completing your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!isSessionReady || !session) return <PageLoader />;

  if (outcome) {
    return (
      <Wrapper>
        <Card>
          <Done>
            <DoneIcon icon={outcome.approved ? 'circle-check' : 'circle-xmark'} />
            {outcome.approved ? (
              <>
                <div>
                  <ClientName>{outcome.client.name}</ClientName> is connected.
                </div>
                {/* There is nowhere to redirect to — the device is polling and will notice. */}
                <Note>You can go back to the device you started this from. It will continue on its own.</Note>
              </>
            ) : (
              <>
                <div>The request was denied.</div>
                <Note>Nothing was connected, and the code can no longer be used.</Note>
              </>
            )}
          </Done>
        </Card>
      </Wrapper>
    );
  }

  // Step one: the member types the code shown on the device.
  if (!request) {
    return (
      <Wrapper>
        <Card>
          <Title>Connect a Device</Title>
          <Section>
            <SectionLabel>Enter the code shown on your device</SectionLabel>
            <CodeInput
              value={userCode}
              placeholder="XXXX-XXXX"
              maxLength={9}
              autoFocus
              onChange={(event) => setUserCode(event.target.value)}
            />
            {errorMessage && <Message>{errorMessage}</Message>}
          </Section>
          <Actions>
            <Buttons align="right">
              <Button disabled={!userCode || isSubmitting} isLoading={isSubmitting} onClick={() => lookup(userCode)}>
                Continue
              </Button>
            </Buttons>
          </Actions>
        </Card>
      </Wrapper>
    );
  }

  // Step two: the same consent decision the redirect flow asks for.
  const hasWorkspace = request.workspaces.length > 0;
  const hasSingleWorkspace = request.workspaces.length === 1;

  return (
    <Wrapper>
      <Card>
        <Title>
          Connect <ClientName>{request.client.name}</ClientName> to Ruddr
        </Title>

        {/* A dynamically registered client chooses its own name, so nothing stops one calling itself
            Ruddr. Only a metadata document ties the name to a domain the member can judge. */}
        {!request.client.isClientIdMetadataDocument && (
          <Unverified>This name was provided by the application and has not been verified by Ruddr.</Unverified>
        )}

        <Section>
          <SectionLabel>This will allow it to</SectionLabel>
          {request.scopes.map(({ scope, description }) => (
            <Scope key={scope}>
              <ScopeIcon icon="check" />
              <span>{description}</span>
            </Scope>
          ))}
        </Section>

        {request.client.isClientIdMetadataDocument && (
          <Section>
            <SectionLabel>Identified by</SectionLabel>
            <Scope>
              <code>{request.client.clientIdHost}</code>
            </Scope>
          </Section>
        )}

        <Section>
          <SectionLabel>Workspace</SectionLabel>
          {hasWorkspace ? (
            /* Disabled rather than replaced when there is only one: the member still sees which
               workspace they are connecting, without being offered a choice that does not exist. */
            <SingleSelect
              value={workspaceId}
              disabled={hasSingleWorkspace}
              placeholder="Select a workspace"
              onChange={(event) => setWorkspaceId(event.target.value)}>
              {request.workspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </option>
              ))}
            </SingleSelect>
          ) : request.hasMemberSession ? (
            <Scope>
              <span>
                None of your workspaces have the Ruddr Assistant enabled. Ask a workspace admin to enable it, then try
                connecting again.
              </span>
            </Scope>
          ) : (
            <Scope>
              <span>
                A device can only be connected by a member signed in to Ruddr directly, not through an administrator's
                impersonation session. Sign in yourself and try connecting again.
              </span>
            </Scope>
          )}
        </Section>

        <Note>
          {request.client.name} will act as you. It can only see and change what your Ruddr permissions already allow,
          and you can revoke this connection at any time from your profile. Only continue if you started this yourself.
        </Note>

        {errorMessage && <Message>{errorMessage}</Message>}

        <Actions>
          <Buttons align="right">
            <CancelButton disabled={isSubmitting} onClick={() => decide(false)} style={{ marginRight: 'auto' }}>
              Deny
            </CancelButton>
            {/* `disabled` has to carry isSubmitting too: Button spreads props over its own
                `disabled={isLoading}`, so passing one here would otherwise re-enable the click. */}
            <Button
              disabled={!workspaceId || !hasWorkspace || isSubmitting}
              isLoading={isSubmitting}
              onClick={() => decide(true)}>
              Authorize
            </Button>
          </Buttons>
        </Actions>
      </Card>
    </Wrapper>
  );
}
